import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Capacitor } from "@capacitor/core";
import { SUBSCRIPTION_PLANS, type SubscriptionPlan } from "@/types/subscription";
import { useRevenueCat } from "@/contexts/RevenueCatContext";
import { purchaseRevenueCatProduct } from "@/utils/revenuecatPurchase";
import { Loader2 } from "lucide-react";

export const SubscriptionPlans = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const isIOS = isNative && Capacitor.getPlatform() === "ios";
  const { isConfigured, isConfiguring, error: revenueCatError } = useRevenueCat();

  // Show message if not on iOS
  useEffect(() => {
    if (!isIOS) {
      toast({
        title: "iOS Only",
        description: "Subscriptions are only available on the iOS app.",
        variant: "destructive",
      });
    }
  }, [isIOS, toast]);

  // Show error if RevenueCat failed to initialize
  useEffect(() => {
    if (revenueCatError) {
      toast({
        title: "Subscription Setup Error",
        description: revenueCatError,
        variant: "destructive",
      });
    }
  }, [revenueCatError, toast]);

  const handleIOSSubscription = async (plan: SubscriptionPlan) => {
    if (isProcessing) return;

    // Check if RevenueCat is configured
    if (!isConfigured) {
      toast({
        title: "Not Ready",
        description: isConfiguring
          ? "Please wait, subscription service is initializing..."
          : "Subscription service is not available. Please restart the app.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      // Purchase (uses Offering packages when configured, otherwise falls back to direct product purchase)
      const { customerInfo } = await purchaseRevenueCatProduct({ productId: plan.id });

      // Check if the purchase was successful
      const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;

      if (hasActiveSubscription) {
        toast({
          title: "Success!",
          description: `You're now subscribed to ${plan.name}`,
        });
      } else {
        throw new Error("Purchase completed but no active entitlement found");
      }
    } catch (error: any) {
      console.error("Subscription error:", error);

      if (error.code === "PURCHASE_CANCELLED" || error.userCancelled) {
        toast({
          title: "Purchase cancelled",
          description: "You cancelled the subscription purchase.",
        });
      } else {
        toast({
          title: "Subscription failed",
          description: error.message || "Please try again later.",
          variant: "destructive",
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state while RevenueCat initializes
  if (isIOS && isConfiguring) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-muted-foreground">Loading subscriptions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center space-y-1 mb-2">
        <h3 className="text-sm font-semibold text-muted-foreground">MindNote Pro — Auto-Renewable Subscriptions</h3>
        <p className="text-[10px] md:text-xs text-muted-foreground">
          Unlock all premium features including AI assistance, speech-to-text, OCR, and more.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {SUBSCRIPTION_PLANS.map((plan) => (
          <Card key={plan.id} className="p-3 md:p-4 flex flex-col">
            <h3 className="text-sm md:text-base font-bold mb-1">{plan.name}</h3>
            <p className="text-base md:text-lg font-bold mb-1 md:mb-2">
              ${plan.price} USD
              <span className="text-[10px] md:text-xs font-normal text-muted-foreground">
                /{plan.interval === 'month' ? 'month' : 'year'}
              </span>
            </p>
            <p className="text-[10px] md:text-xs text-muted-foreground mb-1">
              {plan.interval === 'month'
                ? 'Billed monthly. Full access to all Pro features for 1 month per billing period.'
                : 'Billed yearly. Full access to all Pro features for 12 months per billing period. Save 17%!'}
            </p>
            <p className="text-[10px] md:text-xs text-muted-foreground mb-2 md:mb-3">
              {plan.interval === 'month'
                ? '$9.99 USD per month'
                : '$100.00 USD per year'}
            </p>
            <Button
              onClick={() => handleIOSSubscription(plan)}
              disabled={!isIOS || isProcessing || !isConfigured}
              className="mt-auto w-full text-xs md:text-sm h-9 md:h-10"
            >
              {isProcessing ? "Processing..." : "Subscribe"}
            </Button>
          </Card>
        ))}
      </div>

      <div className="text-[10px] md:text-xs text-muted-foreground text-center space-y-2 pt-3 border-t">
        <h4 className="font-semibold text-foreground text-xs md:text-sm">Subscription Details</h4>
        <div className="text-left space-y-1 px-2">
          <p><span className="font-medium text-foreground">Monthly Plan:</span> MindNote Pro — $9.99 USD/month. Auto-renews monthly. Full access to all Pro features for 1 month per billing period.</p>
          <p><span className="font-medium text-foreground">Yearly Plan:</span> MindNote Pro — $100.00 USD/year. Auto-renews yearly. Full access to all Pro features for 12 months per billing period.</p>
        </div>
        <p>
          Payment will be charged to your Apple ID account at confirmation of purchase.
          Subscription automatically renews unless cancelled at least 24 hours before the end of the current period.
          Your account will be charged for renewal within 24 hours prior to the end of the current period.
          You can manage and cancel your subscriptions in your device's Settings &gt; Apple ID &gt; Subscriptions.
        </p>
        <p className="flex items-center justify-center gap-2 flex-wrap">
          <a
            href="https://www.mindnote.online/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary font-medium"
          >
            Privacy Policy
          </a>
          <span>•</span>
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary font-medium"
          >
            Terms of Use (EULA)
          </a>
        </p>
      </div>
    </div>
  );
};
